// Express (the fast tier) rules for PopCart: pure, shared by the API (authoritative), the customer
// app and Ops. Integer centavos and minutes. Contract: docs/PHASE6-CONTRACT.md.
//
// The guarantee (Bold Pop manual §13 voice): "Sorry, we're 10 minutes late. Your next delivery's on
// us. No small print." A late Express order earns a credit that pays the next order's delivery fee
// and Express surcharge, up to express.lateCreditCentavos. Never cash, never goods.

/** Minutes late (0 when on time). Rounded up: 30 s late is 1 minute late. */
export function lateMinutes(promiseAt, deliveredAt) {
  if (!promiseAt || !deliveredAt) return 0;
  const ms = new Date(deliveredAt).getTime() - new Date(promiseAt).getTime();
  return ms > 0 ? Math.ceil(ms / 60000) : 0;
}

/**
 * Where a live Express run stands against its promise.
 * @param {{ now: Date, promiseAt: string|Date, etaMinutes: number|null, riskMarginMinutes?: number }} p
 *   etaMinutes: the best current estimate of minutes until delivery (null = unknown, e.g. not yet assigned).
 * @returns {{ state: 'on_track'|'at_risk'|'late', minutesLeft: number, projectedLateMinutes: number }}
 */
export function expressRisk({ now, promiseAt, etaMinutes, riskMarginMinutes = 3 }) {
  const left = Math.floor((new Date(promiseAt).getTime() - now.getTime()) / 60000);
  if (left < 0) return { state: 'late', minutesLeft: left, projectedLateMinutes: -left + (etaMinutes ?? 0) };
  if (etaMinutes == null) return { state: left <= riskMarginMinutes ? 'at_risk' : 'on_track', minutesLeft: left, projectedLateMinutes: 0 };
  const projected = etaMinutes - left;
  return { state: projected > -riskMarginMinutes ? 'at_risk' : 'on_track', minutesLeft: left, projectedLateMinutes: Math.max(0, projected) };
}

/** The credit a late Express order earns: express.lateCreditCentavos when late by ≥ 1 minute, else 0. */
export function lateCredit({ promiseAt, deliveredAt, lateCreditCentavos }) {
  return lateMinutes(promiseAt, deliveredAt) > 0 ? Math.max(0, lateCreditCentavos | 0) : 0;
}

/**
 * Spend credits on an order's delivery fee + Express surcharge (never goods). Oldest-expiring first.
 * @param {{ id: string, remainingCentavos: number, expiresAt: string }[]} credits  usable (unexpired, remaining > 0)
 * @param {number} coverableCentavos  delivery fee + Express surcharge of the order
 * @returns {{ appliedCentavos: number, spends: { creditId: string, amountCentavos: number }[] }}
 */
export function applyCredits(credits, coverableCentavos) {
  let left = Math.max(0, coverableCentavos | 0);
  const spends = [];
  const ordered = [...credits].sort((a, b) => new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime() || String(a.id).localeCompare(String(b.id)));
  for (const c of ordered) {
    if (left <= 0) break;
    const take = Math.min(left, c.remainingCentavos);
    if (take > 0) { spends.push({ creditId: c.id, amountCentavos: take }); left -= take; }
  }
  return { appliedCentavos: spends.reduce((t, s) => t + s.amountCentavos, 0), spends };
}

/** Should an unassigned Express run be escalated to Ops? */
export function shouldEscalate({ now, createdAt, escalateAfterSeconds = 120, alreadyEscalated = false }) {
  return !alreadyEscalated && now.getTime() - new Date(createdAt).getTime() >= escalateAfterSeconds * 1000;
}

/** Express capacity gauge: { active, max, full, pct } (max 0 = Express off). */
export function capacity(active, maxActive) {
  const max = Math.max(0, maxActive | 0);
  return { active, max, full: max === 0 || active >= max, pct: max ? Math.min(100, Math.round((active / max) * 100)) : 100 };
}
